import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  MessageCircle, RefreshCw, Loader2, AlertCircle, X, CheckCircle, Search,
  ChevronRight, Save, Trash2, Globe, FileText, Inbox, Settings, Eye, Image as ImageIcon,
  FileText as DocIcon, Video, AlertTriangle, Check, Info, Plus
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  drawdown: 'Drawdown',
  investor_account: 'Investor Account',
  general: 'General',
  drawdown_report: 'Drawdown Report',
};

const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700' },
  published: { label: 'Published', cls: 'bg-green-100 text-green-800' },
};

const HEADER_TYPE_LABELS = {
  none: 'No header',
  text: 'Text header',
  image: 'Image header',
  document: 'Document header',
  video: 'Video header',
};

const WhatsAppTemplatesList = () => {
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);

  const [filterCategory, setFilterCategory] = useState('all');
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null); // detail copy that user is editing
  const [placeholders, setPlaceholders] = useState({ keys: [], groups: [] });
  const [phLoading, setPhLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAddManual, setShowAddManual] = useState(false);
  // Section catalog — 6 tag options for the "Sections" multi-select on the
  // editor. Loaded once from the shared /api/email-templates/section-catalog
  // endpoint so labels stay identical to Email Templates.
  const [sectionOptions, setSectionOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/email-templates/section-catalog`);
        setSectionOptions(data.sections || []);
      } catch { /* leave empty — save still succeeds */ }
    })();
  }, []);

  // Allow deep-links to land on a specific template (used by the "Fix this
  // template" button rendered next to the Drawdown WhatsApp picker).
  const [searchParams] = useSearchParams();
  const deepLinkTemplateId = searchParams.get('template');

  useEffect(() => {
    fetchConfig();
    fetchTemplates();
  }, []);

  // After templates load, honour a `?template={id}` deep link by selecting
  // that template (so "Fix this template" from the drawdown picker opens
  // the editor pre-focused on the row the admin came from).
  useEffect(() => {
    if (!deepLinkTemplateId || !templates.length) return;
    if (selectedId === deepLinkTemplateId) return;
    const match = templates.find((t) => t.id === deepLinkTemplateId);
    if (match) setSelectedId(deepLinkTemplateId);
  }, [deepLinkTemplateId, templates, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    const tpl = templates.find((t) => t.id === selectedId);
    if (tpl) {
      // Deep clone for editing
      setSelected({
        ...tpl,
        variable_mappings: (tpl.variable_mappings || []).map((m) => ({ ...m })),
      });
      loadPlaceholders(tpl.category || 'general');
    }
  }, [selectedId, templates]);

  const fetchConfig = async () => {
    setConfigLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp-config`);
      setConfig(data || null);
    } catch (err) {
      // Non-fatal — let templates page work with empty config state
      setConfig(null);
    } finally {
      setConfigLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp-templates`);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load WhatsApp templates');
    } finally {
      setLoading(false);
    }
  };

  const loadPlaceholders = async (category) => {
    setPhLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp-templates/placeholders/${category}`);
      setPlaceholders({
        keys: data.keys || [],
        groups: data.groups || [],
      });
    } catch (err) {
      setPlaceholders({ keys: [], groups: [] });
    } finally {
      setPhLoading(false);
    }
  };

  const handleSync = async () => {
    setError('');
    setSuccess('');
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_URL}/whatsapp-templates/sync`);
      setSuccess(`Synced ${data.total} template(s) — ${data.inserted} new, ${data.updated} updated.`);
      await fetchTemplates();
      await fetchConfig();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to sync templates from vendor');
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        category: selected.category,
        // Sections list — section-gated dropdowns filter to templates carrying
        // at least one matching tag. Untagged templates are hidden.
        sections: Array.isArray(selected.sections) ? selected.sections : [],
        status: selected.status,
        variable_mappings: (selected.variable_mappings || []).map((m) => ({
          var_key: String(m.var_key),
          prime_placeholder: m.prime_placeholder || '',
          fallback_value: m.fallback_value || '',
        })),
      };
      const { data } = await axios.put(`${API_URL}/whatsapp-templates/${selected.id}`, payload);
      // Refresh list + selection
      setTemplates((prev) => prev.map((t) => (t.id === data.id ? data : t)));
      setSelected({
        ...data,
        variable_mappings: (data.variable_mappings || []).map((m) => ({ ...m })),
      });
      setSuccess('Template saved.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API_URL}/whatsapp-templates/${selected.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== selected.id));
      setSelectedId(null);
      setConfirmDelete(false);
      setSuccess('Template removed from Prime AIF (still exists on vendor).');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const filtered = useMemo(() => {
    let list = templates;
    if (filterCategory !== 'all') {
      list = list.filter((t) => (t.category || 'general') === filterCategory);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(
        (t) =>
          (t.provider_template_name || '').toLowerCase().includes(s) ||
          (t.body_text || '').toLowerCase().includes(s),
      );
    }
    return list;
  }, [templates, filterCategory, search]);

  const lastSynced = config?.last_synced_at
    ? new Date(config.last_synced_at).toLocaleString()
    : 'never';

  if (configLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  // Empty state: no config saved
  if (!config) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="wa-templates-no-config">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center mb-4">
          <MessageCircle className="mr-3" size={32} /> WhatsApp Templates
        </h1>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex items-start">
          <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0 mt-0.5" size={22} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-yellow-900">No WhatsApp configuration saved</p>
            <p className="text-sm text-yellow-800 mt-1">
              Configure your provider (Interakt or Wati) first, then come back here to sync
              and categorise your approved templates.
            </p>
            <Link
              to="/whatsapp-config"
              className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              data-testid="wa-templates-go-to-config"
            >
              <Settings size={14} className="mr-2" /> Go to WhatsApp Configuration
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center" data-testid="wa-templates-title">
            <MessageCircle className="mr-3" size={32} />
            WhatsApp Templates
          </h1>
          <p className="text-gray-600 mt-1">
            {config.provider === 'interakt' ? (
              <>Approved templates registered from <span className="font-medium">Interakt</span></>
            ) : (
              <>Approved templates from <span className="font-medium">{config.provider}</span> · last synced {lastSynced}</>
            )}
          </p>
        </div>
        {config.provider === 'interakt' ? (
          <button
            onClick={() => setShowAddManual(true)}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center"
            data-testid="wa-template-add-manual"
            title="Interakt's public API has no list-templates endpoint — register your already-approved templates here."
          >
            <Plus size={16} className="mr-2" />
            Add template
          </button>
        ) : (
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center disabled:opacity-50"
            data-testid="wa-templates-sync"
          >
            {syncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
            Sync from Vendor
          </button>
        )}
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start" data-testid="wa-templates-success">
          <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
          <div className="flex-1 text-sm text-green-800">{success}</div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-700"><X size={14} /></button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start" data-testid="wa-templates-error">
          <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
          <div className="flex-1 text-sm text-red-800 whitespace-pre-wrap">{error}</div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-700"><X size={14} /></button>
        </div>
      )}

      {/* Filter + search */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex gap-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'drawdown', label: 'Drawdown' },
            { key: 'investor_account', label: 'Investor Account' },
            { key: 'general', label: 'General' },
            { key: 'drawdown_report', label: 'Drawdown Report' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFilterCategory(opt.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filterCategory === opt.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`wa-templates-filter-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search template name or body..."
            className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="wa-templates-search"
          />
        </div>
      </div>

      {/* List + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* List */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            {filtered.length} template{filtered.length === 1 ? '' : 's'}
          </div>
          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="animate-spin text-blue-600" size={24} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center" data-testid="wa-templates-empty">
              <Inbox className="mx-auto text-gray-400 mb-3" size={32} />
              <p className="text-sm text-gray-600 font-medium">No templates yet</p>
              <p className="text-xs text-gray-500 mt-1">
                {config.provider === 'interakt'
                  ? <>Click <strong>Add template</strong> to register an already-approved Interakt template.</>
                  : <>Click <strong>Sync from Vendor</strong> to pull your approved templates.</>}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
              {filtered.map((t) => {
                const isActive = t.id === selectedId;
                const statusMeta = STATUS_META[t.status] || STATUS_META.draft;
                return (
                  <li
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${
                      isActive ? 'bg-blue-50 border-l-4 border-blue-600' : 'border-l-4 border-transparent'
                    }`}
                    data-testid={`wa-templates-row-${t.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate font-mono">{t.provider_template_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                          <Globe size={11} /> {t.language_code || 'en'}
                          <span>·</span>
                          <span>{CATEGORY_LABELS[t.category] || t.category}</span>
                          <span>·</span>
                          <span>{t.variable_count} var{t.variable_count === 1 ? '' : 's'}</span>
                        </p>
                      </div>
                      {t.needs_remap && (
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-amber-100 text-amber-800 flex items-center gap-1"
                          data-testid={`wa-templates-needs-remap-${t.id}`}
                          title="Vendor changed this template's variables — please re-check mappings"
                        >
                          <AlertTriangle size={10} /> Re-check
                        </span>
                      )}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusMeta.cls}`}>
                        {statusMeta.label}
                      </span>
                      <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg p-5 min-h-[400px]" data-testid="wa-templates-detail">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-center text-gray-500 py-16">
              <div>
                <FileText className="mx-auto mb-3" size={36} />
                <p className="text-sm">Select a template from the list to map its variables.</p>
              </div>
            </div>
          ) : (
            <TemplateEditor
              tpl={selected}
              setTpl={setSelected}
              placeholders={placeholders}
              phLoading={phLoading}
              onCategoryChange={(c) => loadPlaceholders(c)}
              sectionOptions={sectionOptions}
              onSave={handleSave}
              saving={saving}
              onDelete={() => setConfirmDelete(true)}
            />
          )}
        </div>
      </div>

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="text-red-600 mr-2" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Remove template?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              This only removes the template (and its variable mappings) from Prime AIF.
              The approved template still exists on {config.provider} and will reappear on next sync.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                data-testid="wa-templates-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                data-testid="wa-templates-delete-confirm"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddManual && (
        <AddManualTemplateModal
          onClose={() => setShowAddManual(false)}
          onCreated={async (tpl) => {
            setShowAddManual(false);
            await fetchTemplates();
            setSelectedId(tpl.id);
            setSuccess(`Registered '${tpl.provider_template_name}'. Map its variables, then publish.`);
          }}
        />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Manual template registration modal (Interakt only — Wati uses Sync).
// Body is pasted verbatim from the approved Interakt template; we just store
// it for the preview. The user-facing rules ({{N}} positional substitution)
// are enforced by the existing mapping UI once variable_count rows are created.
// ----------------------------------------------------------------------
const AddManualTemplateModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    provider_template_name: '',
    language_code: 'en',
    header_type: 'none',
    header_text: '',
    body_text: '',
    footer_text: '',
    variable_count: 0,
    category: 'drawdown',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Auto-detect {{N}} placeholders in the pasted body to suggest variable_count.
  const detectedVarCount = useMemo(() => {
    const matches = (form.body_text || '').match(/\{\{\s*\d+\s*\}\}/g) || [];
    const nums = new Set(matches.map((m) => parseInt(m.replace(/\D/g, ''), 10)).filter((n) => !Number.isNaN(n)));
    return nums.size ? Math.max(...nums) : 0;
  }, [form.body_text]);

  const handleAutoFillCount = () => setForm((f) => ({ ...f, variable_count: detectedVarCount }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        variable_count: Math.max(0, parseInt(form.variable_count || '0', 10) || 0),
        provider_template_name: (form.provider_template_name || '').trim(),
        language_code: (form.language_code || 'en').trim() || 'en',
      };
      if (!payload.provider_template_name) {
        setError('Template name is required.');
        setSaving(false);
        return;
      }
      const { data } = await axios.post(`${API_URL}/whatsapp-templates`, payload);
      onCreated(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to register template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="wa-template-add-manual-modal">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 flex items-center">
            <Plus size={16} className="text-blue-600 mr-2" />
            Register Interakt template
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 flex items-start">
            <Info size={14} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Where to find these values:</strong> Open your approved template in the Interakt
              dashboard (Notifications → Templates). Copy the template name, language, and body verbatim.
              The mapping UI will let you bind each <span className="font-mono">{'{{N}}'}</span> to a Prime AIF placeholder after saving.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Template name *</label>
              <input
                type="text"
                required
                value={form.provider_template_name}
                onChange={(e) => setForm({ ...form, provider_template_name: e.target.value })}
                placeholder="e.g. drawdown_notice_v1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                data-testid="wa-template-add-name"
              />
              <p className="text-[11px] text-gray-500 mt-0.5">Must match the approved name on Interakt exactly.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Language code *</label>
              <input
                type="text"
                required
                value={form.language_code}
                onChange={(e) => setForm({ ...form, language_code: e.target.value })}
                placeholder="en"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                data-testid="wa-template-add-language"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Header type *</label>
              <select
                value={form.header_type}
                onChange={(e) => setForm({ ...form, header_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="wa-template-add-header-type"
              >
                <option value="none">No header</option>
                <option value="text">Text header</option>
                <option value="image">Image header</option>
                <option value="document">Document header (attaches PDF)</option>
                <option value="video">Video header</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Category *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="wa-template-add-category"
              >
                <option value="drawdown">Drawdown</option>
                <option value="investor_account">Investor Account</option>
                <option value="general">General</option>
                <option value="drawdown_report">Drawdown Report</option>
              </select>
            </div>
          </div>

          {form.header_type === 'text' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Header text</label>
              <input
                type="text"
                value={form.header_text}
                onChange={(e) => setForm({ ...form, header_text: e.target.value })}
                placeholder="e.g. Capital Call · {{1}}"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="wa-template-add-header-text"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Body text * <span className="font-normal text-gray-500 normal-case">(paste from Interakt verbatim — use <span className="font-mono">{'{{1}}'}</span>, <span className="font-mono">{'{{2}}'}</span>, ... for variables)</span>
            </label>
            <textarea
              required
              rows={5}
              value={form.body_text}
              onChange={(e) => setForm({ ...form, body_text: e.target.value })}
              placeholder={'Dear {{1}}, drawdown {{2}} of {{3}} is due on {{4}}.'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              data-testid="wa-template-add-body"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Footer text (optional)</label>
            <input
              type="text"
              value={form.footer_text}
              onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
              placeholder="e.g. — Prime AIF"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="wa-template-add-footer"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">Variable count *</label>
              {detectedVarCount > 0 && parseInt(form.variable_count || '0', 10) !== detectedVarCount && (
                <button
                  type="button"
                  onClick={handleAutoFillCount}
                  className="text-xs text-blue-600 hover:underline"
                  data-testid="wa-template-add-variable-autodetect"
                >
                  Detected {detectedVarCount} in body — use this
                </button>
              )}
            </div>
            <input
              type="number"
              min={0}
              required
              value={form.variable_count}
              onChange={(e) => setForm({ ...form, variable_count: e.target.value })}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="wa-template-add-variable-count"
            />
            <p className="text-[11px] text-gray-500 mt-0.5">
              Number of <span className="font-mono">{'{{N}}'}</span> placeholders in the body. We&apos;ll create one mapping row per variable for you.
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start text-sm" data-testid="wa-template-add-error">
              <AlertCircle size={14} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-red-800 whitespace-pre-wrap">{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
            data-testid="wa-template-add-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 flex items-center disabled:opacity-50"
            data-testid="wa-template-add-submit"
          >
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
            Register template
          </button>
        </div>
      </form>
    </div>
  );
};

// ----------------------------------------------------------------------
const TemplateEditor = ({ tpl, setTpl, placeholders, phLoading, onCategoryChange, sectionOptions = [], onSave, saving, onDelete }) => {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewData, setPreviewData] = useState(null);

  const handleMappingChange = (idx, field, value) => {
    setTpl((prev) => {
      const next = { ...prev };
      next.variable_mappings = (prev.variable_mappings || []).map((m, i) =>
        i === idx ? { ...m, [field]: value } : m,
      );
      return next;
    });
  };

  const handlePreview = async () => {
    setPreviewError('');
    setPreviewLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/whatsapp-templates/${tpl.id}/preview`);
      setPreviewData(data);
    } catch (err) {
      setPreviewError(err.response?.data?.detail || 'Failed to render preview');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderBodyPreview = () => {
    if (!tpl.body_text) return <em className="text-gray-400">(empty body)</em>;
    // Highlight {{N}} and named {{key}} positions
    const parts = tpl.body_text.split(/(\{\{\s*[A-Za-z0-9_\-]+\s*\}\})/g);
    return parts.map((p, i) => {
      if (/^\{\{\s*[A-Za-z0-9_\-]+\s*\}\}$/.test(p)) {
        return (
          <span key={i} className="bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-mono text-xs">
            {p}
          </span>
        );
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div className="space-y-5">
      {tpl.needs_remap && (
        <div
          className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start"
          data-testid="wa-template-needs-remap-banner"
        >
          <AlertTriangle className="text-amber-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
          <div className="flex-1 text-xs text-amber-900">
            <p className="font-semibold">Re-check needed.</p>
            <p className="mt-0.5">
              The vendor changed this template&apos;s variables on the last sync. Stale mappings were
              dropped and the template was reverted to <strong>Draft</strong>. Review the
              mappings below, then save (and re-publish if you&apos;re ready).
            </p>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 font-mono break-all" data-testid="wa-template-name">
            {tpl.provider_template_name}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
            <Globe size={11} /> {tpl.language_code}
            <span>·</span>
            <span>{HEADER_TYPE_LABELS[tpl.header_type] || tpl.header_type}</span>
            <span>·</span>
            <span>{tpl.variable_count} var{tpl.variable_count === 1 ? '' : 's'}</span>
            {tpl.vendor_status && (<><span>·</span><span className="uppercase text-green-700 font-medium">{tpl.vendor_status}</span></>)}
          </p>
        </div>
        <button
          onClick={onDelete}
          className="text-red-600 hover:bg-red-50 p-2 rounded-md text-sm flex items-center"
          data-testid="wa-template-delete-btn"
          title="Remove from Prime AIF"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Read-only body preview */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
          Approved Body (read-only)
        </label>
        {tpl.header_type !== 'none' && tpl.header_text && (
          <div className="mb-2 p-2 bg-gray-50 border border-gray-200 rounded text-sm font-semibold" data-testid="wa-template-header">
            {tpl.header_text}
          </div>
        )}
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm whitespace-pre-wrap leading-relaxed" data-testid="wa-template-body">
          {renderBodyPreview()}
        </div>
        {tpl.footer_text && (
          <p className="text-xs text-gray-500 mt-1 italic">{tpl.footer_text}</p>
        )}
      </div>

      {/* Category + status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Category *</label>
          <select
            value={tpl.category}
            onChange={(e) => {
              const v = e.target.value;
              setTpl((prev) => ({ ...prev, category: v }));
              onCategoryChange(v);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="wa-template-category"
          >
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Status</label>
          <div className="flex gap-2">
            {Object.entries(STATUS_META).map(([k, meta]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTpl((prev) => ({ ...prev, status: k }))}
                className={`flex-1 px-3 py-2 text-xs font-medium border rounded-md transition-colors ${
                  tpl.status === k
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`wa-template-status-${k}`}
              >
                {meta.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Section tags — controls which send-time dropdowns can pick this template. */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
          Sections <span className="text-gray-400 font-normal normal-case tracking-normal">(select where this template can be used)</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="wa-template-sections">
          {sectionOptions.map((s) => {
            const checked = (tpl.sections || []).includes(s.value);
            return (
              <label
                key={s.value}
                className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${
                  checked ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                }`}
                data-testid={`wa-template-section-opt-${s.value}`}
              >
                <input
                  type="checkbox"
                  className="rounded"
                  checked={checked}
                  onChange={(e) => {
                    setTpl((prev) => {
                      const cur = new Set(prev.sections || []);
                      if (e.target.checked) cur.add(s.value); else cur.delete(s.value);
                      return { ...prev, sections: Array.from(cur) };
                    });
                  }}
                />
                <span className="text-xs font-medium">{s.label}</span>
              </label>
            );
          })}
        </div>
        {(!tpl.sections || tpl.sections.length === 0) && (
          <p className="text-[11px] text-amber-600 mt-1.5">
            Tag at least one section — untagged templates are hidden from every section&apos;s dropdown.
          </p>
        )}
      </div>

      {/* Variable mappings */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
            Variable Mappings
          </label>
          <button
            type="button"
            onClick={handlePreview}
            disabled={previewLoading}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="wa-template-preview"
          >
            {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
            Preview message
          </button>
        </div>
        {(!tpl.variable_mappings || tpl.variable_mappings.length === 0) ? (
          <p className="text-xs text-gray-500 italic">This template has no variables.</p>
        ) : (
          <div className="space-y-2">
            {tpl.variable_mappings.map((m, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded p-2 border border-gray-200" data-testid={`wa-template-mapping-${m.var_key}`}>
                <div className="col-span-2">
                  <span className="text-xs font-mono bg-amber-100 text-amber-800 px-2 py-1 rounded">
                    {`{{${m.var_key}}}`}
                  </span>
                </div>
                <div className="col-span-5">
                  <PlaceholderPicker
                    value={m.prime_placeholder}
                    onChange={(v) => handleMappingChange(idx, 'prime_placeholder', v)}
                    placeholderKeys={placeholders.keys}
                    phLoading={phLoading}
                    testid={`wa-template-placeholder-${m.var_key}`}
                  />
                </div>
                <div className="col-span-5">
                  <input
                    type="text"
                    value={m.fallback_value || ''}
                    onChange={(e) => handleMappingChange(idx, 'fallback_value', e.target.value)}
                    placeholder={tpl.provider === 'interakt' ? 'Fallback (required by Interakt)' : 'Fallback value (optional)'}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid={`wa-template-fallback-${m.var_key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Pick a Prime AIF placeholder for each variable position — or combine multiple with literal text
          (e.g. <code className="bg-gray-100 px-1 rounded">{`{{salutation}} {{last_name}}`}</code> resolves to <em>Mr. Sharma</em>). The fallback is used when the placeholder resolves to empty at send time.
          {tpl.provider === 'interakt' && (
            <span className="text-amber-700 font-medium"> Interakt requires non-empty fallbacks before publishing.</span>
          )}
        </p>
      </div>

      <div className="flex justify-end pt-3 border-t border-gray-100">
        <button
          onClick={onSave}
          disabled={saving}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center disabled:opacity-50"
          data-testid="wa-template-save-btn"
        >
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
          Save Template
        </button>
      </div>

      {/* Preview modal */}
      {(previewData || previewError) && (
        <PreviewModal
          tpl={tpl}
          data={previewData}
          error={previewError}
          onClose={() => { setPreviewData(null); setPreviewError(''); }}
        />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
const PreviewModal = ({ tpl, data, error, onClose }) => {
  const headerIcon = (hType) => {
    if (hType === 'image') return <ImageIcon size={14} className="text-gray-500" />;
    if (hType === 'document') return <DocIcon size={14} className="text-gray-500" />;
    if (hType === 'video') return <Video size={14} className="text-gray-500" />;
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="wa-template-preview-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <MessageCircle size={16} className="text-blue-600 mr-2" />
            <h3 className="text-sm font-bold text-gray-900">Preview · WhatsApp message</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="wa-template-preview-close">
            <X size={16} />
          </button>
        </div>

        {error ? (
          <div className="p-5 text-sm text-red-700 bg-red-50">{error}</div>
        ) : (
          <>
            {/* Warning when some vars couldn't resolve */}
            {data && data.unresolved && data.unresolved.length > 0 && (
              <div
                className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-start text-xs text-amber-900"
                data-testid="wa-template-preview-unresolved"
              >
                <AlertTriangle size={12} className="text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  {data.unresolved.length} variable{data.unresolved.length === 1 ? '' : 's'} couldn&apos;t be previewed — check mappings for{' '}
                  <span className="font-mono">{data.unresolved.map((k) => `{{${k}}}`).join(', ')}</span>.
                </span>
              </div>
            )}

            {/* WhatsApp-style bubble */}
            <div className="p-5 bg-[#ECE5DD]">
              <div
                className="max-w-[85%] ml-auto bg-[#DCF8C6] rounded-lg rounded-tr-sm shadow-sm overflow-hidden"
                data-testid="wa-template-preview-bubble"
              >
                {/* Header (only text headers render verbatim; media headers show a placeholder card) */}
                {data?.header_type === 'text' && data.header_rendered && (
                  <div className="px-3 pt-3 pb-1 text-[15px] font-semibold text-gray-900" data-testid="wa-template-preview-header">
                    {data.header_rendered}
                  </div>
                )}
                {data && ['image', 'document', 'video'].includes(data.header_type) && (
                  <div className="mx-3 mt-3 mb-1 px-3 py-2 bg-white/60 border border-white rounded flex items-center text-xs text-gray-600 gap-2" data-testid="wa-template-preview-header-media">
                    {headerIcon(data.header_type)}
                    <span className="capitalize">{data.header_type} attachment</span>
                  </div>
                )}

                {/* Body */}
                <div className="px-3 py-2 text-[14px] text-gray-900 whitespace-pre-wrap leading-snug" data-testid="wa-template-preview-body">
                  {data?.body_rendered || ''}
                </div>

                {/* Footer */}
                {data?.footer_rendered && (
                  <div className="px-3 pb-1 text-[11px] text-gray-500 italic" data-testid="wa-template-preview-footer">
                    {data.footer_rendered}
                  </div>
                )}

                {/* WhatsApp-style timestamp + read tick */}
                <div className="px-3 pb-2 text-right text-[10px] text-gray-500 flex items-center justify-end gap-0.5">
                  <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  <Check size={10} className="text-blue-500" />
                  <Check size={10} className="text-blue-500 -ml-1.5" />
                </div>
              </div>
            </div>

            {/* Caption */}
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-[11px] text-gray-500 flex items-center gap-1">
              <Info size={10} className="text-gray-400" />
              Rendered using sample data ({data?.sample_context_type}). The actual message uses the
              recipient&apos;s data at send time.
            </div>
          </>
        )}

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            data-testid="wa-template-preview-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
const PlaceholderPicker = ({ value, onChange, placeholderKeys, phLoading, testid }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = React.useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return placeholderKeys.slice(0, 40);
    const q = query.toLowerCase();
    return placeholderKeys.filter((k) => k.toLowerCase().includes(q)).slice(0, 40);
  }, [placeholderKeys, query]);

  // Detect whether the current value is a multi-field template
  // (e.g. `{{salutation}} {{last_name}}`) or a bare single placeholder.
  const placeholderMatches = (value || '').match(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g) || [];
  const trimmed = (value || '').trim();
  const bareKey = !trimmed.includes('{{') && trimmed.length > 0;
  const isSingleFullMatch = placeholderMatches.length === 1
    && placeholderMatches[0].replace(/\s+/g, '') === trimmed.replace(/\s+/g, '');
  const isCompositeTemplate = placeholderMatches.length > 0 && !isSingleFullMatch;

  const insertField = (key) => {
    const token = `{{${key}}}`;
    const el = inputRef.current;
    const cur = value || '';
    if (el && typeof el.selectionStart === 'number') {
      const s = el.selectionStart;
      const e = el.selectionEnd;
      const next = cur.slice(0, s) + token + cur.slice(e);
      onChange(next);
      // Restore caret after the inserted token on the next tick.
      requestAnimationFrame(() => {
        if (el && typeof el.setSelectionRange === 'function') {
          const pos = s + token.length;
          el.focus();
          el.setSelectionRange(pos, pos);
        }
      });
    } else {
      // No input ref yet (or unsupported): append.
      onChange((cur ? cur + ' ' : '') + token);
    }
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative">
      <div className="flex items-stretch gap-1">
        <input
          ref={inputRef}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Select placeholder or type {{field1}} {{field2}}..."
          className={`flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            value ? 'text-blue-700 font-mono' : 'text-gray-700'
          }`}
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white hover:bg-blue-50 text-blue-700 font-medium flex-shrink-0"
          title="Insert a Prime AIF placeholder"
          data-testid={`${testid}-insert-btn`}
        >
          + Field
        </button>
      </div>
      {isCompositeTemplate && (
        <p className="text-[10px] text-blue-600 mt-0.5" data-testid={`${testid}-multi-field-hint`}>
          Multi-field template — every {`{{key}}`} resolves at send time. Literal text between braces is preserved.
        </p>
      )}
      {bareKey && !placeholderKeys.includes(trimmed) && (
        <p className="text-[10px] text-amber-600 mt-0.5" data-testid={`${testid}-unknown-key-hint`}>
          &ldquo;{trimmed}&rdquo; isn&apos;t a known Prime AIF field — wrap valid keys in {`{{ }}`} for composite templates.
        </p>
      )}
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-72 overflow-y-auto">
          <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search placeholders to insert..."
              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-blue-500"
              autoFocus
              data-testid={`${testid}-search`}
            />
            <p className="text-[10px] text-gray-500 mt-1 leading-tight">
              Tip: click a field to insert {`{{key}}`} at your cursor. Combine several to build composite values like <code className="bg-gray-100 px-1 rounded">{`{{salutation}} {{last_name}}`}</code>.
            </p>
          </div>
          {phLoading ? (
            <div className="p-3 text-xs text-gray-500 flex items-center"><Loader2 size={12} className="animate-spin mr-2" /> Loading placeholders...</div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-xs text-gray-500">No matches</div>
          ) : (
            <>
              {value && (
                <button
                  type="button"
                  onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                  className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 border-b border-gray-100"
                  data-testid={`${testid}-clear`}
                >
                  Clear value
                </button>
              )}
              {filtered.map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => insertField(k)}
                  className="w-full px-3 py-1.5 text-left text-xs font-mono hover:bg-blue-50 text-gray-700"
                >
                  {`{{${k}}}`}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplatesList;
